import React, { useCallback, useState } from 'react';
import Heading from '@atlaskit/heading';
import { Button } from 'view/common/analytics';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import { DialogContentWrapper, DialogHeadingWrapper, RemoveHelpDeskWrapper } from './styled';
import { useIntl } from 'react-intl-next';
import messages from './messages';
import { graphql } from 'relay-runtime';
import { useMutation } from 'react-relay';
import type { removeHelpDeskMutation } from './__generated__/removeHelpDeskMutation.graphql';
import { getCloudId, getHelpCenterAri } from 'util/meta';
import { useRouterActions } from 'react-resource-router';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { EventTypes, TRACK_EVENT_TYPE, fireAnalyticsEvent } from '@atlassian/analytics-web-react';
import { showErrorFlag, showSuccessFlag } from 'state/actions/flags';
import { connect } from 'react-redux';
import { deleteHelpDeskExperience } from '../experiences';

const RemoveHelpDesk: React.FC<{
    helpDeskId: string;
    helpDeskName?: string;
    showErrorFlag: typeof showErrorFlag;
    showSuccessFlag: typeof showSuccessFlag;
}> = ({ helpDeskId, helpDeskName, showErrorFlag: showErrorFlagAction, showSuccessFlag: showSuccessFlagAction }) => {
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const { formatMessage } = useIntl();
    const { push } = useRouterActions();
    const openDeleteDialog = useCallback(() => {
        setIsDeleteDialogOpen(true);
    }, []);

    const closeDeleteDialog = useCallback((_, analyticsEvent: UIAnalyticsEvent) => {
        analyticsEvent?.update({
            actionSubjectId: 'cancel-help-desk-delete-modal',
        });

        fireAnalyticsEvent({
            analyticsEvent,
            type: EventTypes.UI,
            payload: {},
        });

        setIsDeleteDialogOpen(false);
    }, []);

    const { createAnalyticsEvent } = useAnalyticsEvents();

    const [deleteHelpDesk] = useMutation<removeHelpDeskMutation>(graphql`
        mutation removeHelpDeskMutation($cloudId: ID!, $input: HelpCenterHelpDeskDeleteInput!) {
            helpCenter(cloudId: $cloudId) {
                deleteHelpDesk(input: $input) {
                    success
                    errors {
                        message
                    }
                }
            }
        }
    `);

    const onDelete = useCallback(
        (_, analyticsEvent: UIAnalyticsEvent) => {
            analyticsEvent?.update({
                actionSubjectId: 'submit-help-desk-delete-modal',
            });

            fireAnalyticsEvent({
                analyticsEvent,
                type: EventTypes.UI,
                payload: {},
            });

            deleteHelpDeskExperience.start() as unknown as void;

            deleteHelpDesk({
                variables: {
                    cloudId: getCloudId(),
                    input: {
                        helpDeskId,
                        helpCenterAri: getHelpCenterAri(),
                    },
                },
                onError: () => {
                    setIsDeleteDialogOpen(false);
                    createAnalyticsEvent({
                        analyticsType: TRACK_EVENT_TYPE,
                        action: 'delete_failed',
                        actionSubject: 'help-desk',
                    }).fire();
                    deleteHelpDeskExperience.failure() as unknown as void;
                    showErrorFlagAction(
                        messages.helpDeskDeleteFailureTitle,
                        messages.helpDeskDeleteFailureDesc,
                        undefined,
                        { value: helpDeskName }
                    );
                },
                updater: (store, response) => {
                    if (response.helpCenter?.deleteHelpDesk?.success) {
                        const root = store.getRoot();
                        const helpCenter = root.getLinkedRecord('helpCenter', {
                            cloudId: getCloudId(),
                        });
                        const UHHelpCenter = helpCenter?.getLinkedRecord('helpCenterById', {
                            helpCenterAri: getHelpCenterAri(),
                        });

                        const helpDesksRecord = UHHelpCenter?.getLinkedRecord('helpDesks');

                        helpDesksRecord?.invalidateRecord();

                        createAnalyticsEvent({
                            helpDeskId,
                            analyticsType: TRACK_EVENT_TYPE,
                            action: 'deleted',
                            actionSubject: 'help-desk',
                        }).fire();

                        deleteHelpDeskExperience.success() as unknown as void;

                        showSuccessFlagAction(messages.helpDeskDeleteSuccessTitle, {
                            value: helpDeskName,
                        });

                        /** Take the user to the home page in next tick */
                        setTimeout(() => {
                            // eslint-disable-next-line @typescript-eslint/no-floating-promises
                            push('/');
                        }, 0);
                    }
                    setIsDeleteDialogOpen(false);
                },
            });
        },
        [
            createAnalyticsEvent,
            deleteHelpDesk,
            helpDeskId,
            helpDeskName,
            push,
            showErrorFlagAction,
            showSuccessFlagAction,
        ]
    );

    const modalActions = [
        {
            text: formatMessage(messages.helpDeskDeleteDialogConfirmButton),
            onClick: onDelete,
        },
        {
            text: formatMessage(messages.helpDeskDeleteDialogCancelButton),
            onClick: closeDeleteDialog,
        },
    ];

    return (
        <RemoveHelpDeskWrapper>
            <Heading level="h600">{formatMessage(messages.helpDeskDeleteSectionTitle)}</Heading>
            <p>{formatMessage(messages.helpDeskDeleteSectionDescription)}</p>

            <Button actionSubjectId="delete-help-desk" onClick={openDeleteDialog}>
                {formatMessage(messages.helpDeskDeleteButton)}
            </Button>

            <ModalTransition>
                {isDeleteDialogOpen && (
                    <Modal width="small" appearance="danger" actions={modalActions} onClose={closeDeleteDialog}>
                        <DialogHeadingWrapper>
                            <Heading level="h600">{formatMessage(messages.helpDeskDeleteDialogTitle)}</Heading>
                        </DialogHeadingWrapper>
                        <DialogContentWrapper>
                            {formatMessage(messages.helpDeskDeleteDialogDescription)}
                        </DialogContentWrapper>
                    </Modal>
                )}
            </ModalTransition>
        </RemoveHelpDeskWrapper>
    );
};

export default connect(null, {
    showErrorFlag,
    showSuccessFlag,
})(RemoveHelpDesk);
